import type { Nullable } from '@ats/interfaces';
import { Layout, MeetingMode } from '@interfaces/chime.enum';
import { useLogger } from '@providers/LoggerProvider';
import { VideoPriorityBasedPolicy } from 'amazon-chime-sdk-js';
import type { ReactNode } from 'react';
import React, { createContext, useContext, useState } from 'react';
import type { JoinInfoDTO } from 'server/model/meeting.room.dto';

type Props = {
  children: ReactNode;
};

interface AppStateValue {
  localUserName: string;
  region: string;
  isWebAudioEnabled: boolean;
  meetingMode: MeetingMode;
  priorityBasedPolicy: VideoPriorityBasedPolicy | undefined;
  layout: Layout;
  joinInfo?: Nullable<JoinInfoDTO>;
  toggleWebAudio: () => void;
  togglePriorityBasedPolicy: () => void;
  setMeetingMode: React.Dispatch<React.SetStateAction<MeetingMode>>;
  setJoinInfo: (joinInfo: Nullable<JoinInfoDTO> | undefined) => void;
  setLayout: React.Dispatch<React.SetStateAction<Layout>>;
  setLocalUserName: React.Dispatch<React.SetStateAction<string>>;
  setRegion: React.Dispatch<React.SetStateAction<string>>;
}

const AppStateContext = createContext<Nullable<AppStateValue>>(null);

export function useAppState(): AppStateValue {
  const state = useContext(AppStateContext);

  if (!state) {
    throw new Error('useAppState must be used within AppStateProvider');
  }

  return state;
}

export function AppStateProvider({ children }: Props) {
  const logger = useLogger();
  const [region, setRegion] = useState('');
  const [meetingMode, setMeetingMode] = useState(MeetingMode.Attendee);
  const [joinInfo, setJoinInfo] = useState<Nullable<JoinInfoDTO> | undefined>(
    null,
  );
  const [layout, setLayout] = useState(Layout.Gallery);
  const [localUserName, setLocalUserName] = useState('');
  const [isWebAudioEnabled, setIsWebAudioEnabled] = useState(true);
  const [priorityBasedPolicy, setPriorityBasedPolicy] = useState<
    VideoPriorityBasedPolicy | undefined
  >(undefined);

  const toggleWebAudio = (): void => {
    setIsWebAudioEnabled((current) => !current);
  };

  const togglePriorityBasedPolicy = (): void => {
    if (priorityBasedPolicy) {
      setPriorityBasedPolicy(undefined);
    } else {
      setPriorityBasedPolicy(new VideoPriorityBasedPolicy(logger));
    }
  };

  const providerValue = {
    localUserName,
    isWebAudioEnabled,
    region,
    meetingMode,
    layout,
    joinInfo,
    priorityBasedPolicy,
    toggleWebAudio,
    togglePriorityBasedPolicy,
    setMeetingMode,
    setLayout,
    setJoinInfo,
    setLocalUserName,
    setRegion,
  };

  return (
    <AppStateContext.Provider value={providerValue}>
      {children}
    </AppStateContext.Provider>
  );
}
